import {
    AppendixImage,
    ProjectData,
    RabEntry,
    UnterlageVersion,
    VorankuendigungArchiveEntry,
    VorankuendigungData,
} from '../types';

/**
 * Versionsverwaltung für Vorankündigung und Unterlage für spätere Arbeiten.
 *
 * Alle Funktionen sind rein: sie geben neue Objekte/Arrays zurück und fassen
 * bestehende Versionen nie an. Das ist die Voraussetzung dafür, dass die
 * verzögerte Autospeicherung der Editoren einen Versionsstand nicht
 * nachträglich überschreiben kann.
 */

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

/** Zeitstempel als "TT.MM.JJJJ, HH:MM" — Fallback-Beschriftung ohne eigenen Namen. */
export const formatVersionTimestamp = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

/** Beschriftung säubern; leere Eingabe ergibt eine Beschriftung aus dem Zeitstempel. */
export const normalizeVersionLabel = (label: string | undefined | null, createdAt: string): string => {
    const clean = (label || '').trim();
    return clean || `Stand ${formatVersionTimestamp(createdAt)}`;
};

// ── Vorankündigung ──────────────────────────────────────────────────────────

/**
 * Erzeugt einen Archiveintrag aus dem aktuellen Stand.
 *
 * Das Archiv selbst wird aus dem Schnappschuss entfernt: sonst enthielte jeder
 * Eintrag sämtliche Vorgänger und der Report-JSON wüchse exponentiell.
 */
export const createVorankuendigungVersion = (
    data: VorankuendigungData,
    label: string | undefined,
    createdAt: string,
    id: string,
): VorankuendigungArchiveEntry => {
    const snapshot = clone(data);
    delete snapshot.archive;
    return {
        id,
        date: createdAt,
        label: normalizeVersionLabel(label, createdAt),
        snapshot,
    };
};

/** Neuen Eintrag voranstellen (neueste Version zuerst). */
export const appendVorankuendigungVersion = (
    archive: VorankuendigungArchiveEntry[] | undefined,
    entry: VorankuendigungArchiveEntry,
): VorankuendigungArchiveEntry[] => [entry, ...(archive || [])];

/**
 * Version in den Editor zurückholen.
 *
 * Archiv und Verlauf des laufenden Dokuments bleiben erhalten — das Laden
 * ersetzt den Inhalt, nicht die Historie.
 */
export const restoreVorankuendigungVersion = (
    current: VorankuendigungData,
    entry: VorankuendigungArchiveEntry,
): VorankuendigungData => ({
    ...clone(entry.snapshot),
    history: current.history || [],
    archive: current.archive || [],
});

/** Anzeigename eines Archiveintrags (eigener Name, sonst Zeitstempel). */
export const describeVorankuendigungVersion = (entry: VorankuendigungArchiveEntry): string =>
    normalizeVersionLabel(entry.label, entry.date);

// ── Unterlage für spätere Arbeiten ──────────────────────────────────────────

/**
 * Setzt die Anhangsbilder des LAUFENDEN Dokuments auf einen Versionsstand.
 *
 * Bilder gehören nicht zur Version (siehe `createUnterlageVersion`), sondern
 * zum lebenden Dokument. Jeder Weg, der aus einer Version wieder ein
 * vollständiges Dokument macht — Zurückholen in den Editor, PDF einer Version —
 * muss sie deshalb von dort ergänzen.
 */
export const withLiveAppendixImages = (
    snapshot: ProjectData,
    liveAppendixImages: AppendixImage[] | undefined,
): ProjectData => ({
    ...clone(snapshot),
    appendixImages: clone(liveAppendixImages || []),
});

/**
 * Friert Projektdaten und Maßnahmenkatalog ein — OHNE Anhangsbilder.
 *
 * Bilder können base64-Rohdaten tragen und liegen normalisiert in
 * `ufsa_appendix_images`; `saveProjectToCloud` streicht sie darum aus dem
 * Projekt-JSON. Ein Schnappschuss mit Bildern würde diese Verschlankung
 * umgehen und die Projektzeile mit jeder Version weiter aufblähen. Das Feld
 * bleibt gesetzt (leeres Array), damit der Typ vollständig bleibt.
 */
export const createUnterlageVersion = (
    projectData: ProjectData,
    rabEntries: RabEntry[],
    label: string | undefined,
    createdAt: string,
    id: string,
): UnterlageVersion => ({
    id,
    label: normalizeVersionLabel(label, createdAt),
    createdAt,
    projectData: { ...clone(projectData), appendixImages: [] },
    rabEntries: clone(rabEntries),
});

export const appendUnterlageVersion = (
    versions: UnterlageVersion[] | undefined,
    version: UnterlageVersion,
): UnterlageVersion[] => [version, ...(versions || [])];

/**
 * Version in den Editor zurückholen — tiefe Kopie, damit der Stand unangetastet
 * bleibt. Die Anhangsbilder des laufenden Dokuments bleiben erhalten: sie sind
 * nicht Teil der Version und würden sonst beim Laden verschwinden.
 */
export const restoreUnterlageVersion = (
    version: UnterlageVersion,
    liveAppendixImages: AppendixImage[] | undefined,
): { projectData: ProjectData; rabEntries: RabEntry[] } => ({
    projectData: withLiveAppendixImages(version.projectData, liveAppendixImages),
    rabEntries: clone(version.rabEntries),
});

export const removeUnterlageVersion = (
    versions: UnterlageVersion[] | undefined,
    id: string,
): UnterlageVersion[] => (versions || []).filter(v => v.id !== id);

/** Beschriftung für die automatische Sicherung vor einer Neuanlage. */
export const autoBackupLabel = (createdAt: string): string =>
    `Automatisch gesichert vor Neuanlage (${formatVersionTimestamp(createdAt)})`;
